import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { GA_MEASUREMENT_ID } from '../utils/analytics';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import './AnalyticsPage.css';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { role: userRole } = useAdminRole();
  const [analyticsLoaded, setAnalyticsLoaded] = useState(false);
  const analyticsContainerRef = useRef(null);

  // Vérifier l'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
      if (!currentUser) {
        navigate('/admin');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Charger le script Google Analytics Embed API
  useEffect(() => {
    if (!user || analyticsLoaded) return;

    const loadAnalyticsScript = () => {
      if (window.gapi && window.gapi.analytics) {
        setAnalyticsLoaded(true);
        return;
      }

      // Charger le script gapi
      if (!document.getElementById('gapi-script')) {
        const script = document.createElement('script');
        script.id = 'gapi-script';
        script.src = 'https://apis.google.com/js/api.js';
        script.onload = () => {
          if (window.gapi) {
            window.gapi.load('auth2', () => {
              window.gapi.load('analytics', () => {
                setAnalyticsLoaded(true);
              });
            });
          }
        };
        document.head.appendChild(script);
      } else if (window.gapi) {
        window.gapi.load('auth2', () => {
          window.gapi.load('analytics', () => {
            setAnalyticsLoaded(true);
          });
        });
      }
    };

    loadAnalyticsScript();
  }, [user, analyticsLoaded]);

  if (loading) {
    return (
      <div className="analytics-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Project ID Firebase
  const GA_PROPERTY_ID = 'aqua-farina';

  return (
    <div className="analytics-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="analytics-content">
        <div className="analytics-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          
          <h1>Google Analytics</h1>
        </div>

        <div className="analytics-body">
          <div className="analytics-info-card">
            <h2>Statistiques du site web</h2>
            <p>
              Les données Analytics sont collectées via Firebase Analytics (Google Analytics 4).
              Consultez les statistiques détaillées dans la console Firebase ou Google Analytics.
            </p>
            <div className="analytics-links">
              <a 
                href={`https://console.firebase.google.com/project/${GA_PROPERTY_ID}/analytics`}
                target="_blank"
                rel="noopener noreferrer"
                className="analytics-link-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Ouvrir Firebase Analytics
              </a>
              <a 
                href={`https://analytics.google.com/analytics/web/#/p${GA_MEASUREMENT_ID.replace('G-', '')}/reports/intelligenthome`}
                target="_blank"
                rel="noopener noreferrer"
                className="analytics-link-btn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
                Ouvrir Google Analytics
              </a>
            </div>
          </div>

          <div className="analytics-embed-container">
            <h2>Rapport Analytics en direct</h2>
            <div className="analytics-dashboard-wrapper">
              <div className="analytics-widgets-grid">
                <div className="analytics-widget">
                  <h3>Vues de pages (7 derniers jours)</h3>
                  <div className="widget-content">
                    <div className="widget-placeholder">
                      <p>Les données Analytics sont collectées en temps réel.</p>
                      <p className="widget-note">
                        Pour voir les rapports détaillés, utilisez les liens ci-dessus ou consultez directement Google Analytics.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="analytics-widget">
                  <h3>Événements récents</h3>
                  <div className="widget-content">
                    <div className="widget-placeholder">
                      <p>Événements trackés :</p>
                      <ul className="widget-events-list">
                        <li>✓ Vues de pages</li>
                        <li>✓ Clics sur le menu</li>
                        <li>✓ Réservations</li>
                        <li>✓ Clics sur les directions</li>
                        <li>✓ Appels téléphoniques</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="analytics-widget">
                  <h3>Statistiques rapides</h3>
                  <div className="widget-content">
                    <div className="widget-placeholder">
                      <div className="quick-stats">
                        <div className="quick-stat-item">
                          <span className="stat-label">Measurement ID</span>
                          <span className="stat-value">{GA_MEASUREMENT_ID}</span>
                        </div>
                        <div className="quick-stat-item">
                          <span className="stat-label">Type</span>
                          <span className="stat-value">GA4</span>
                        </div>
                        <div className="quick-stat-item">
                          <span className="stat-label">Status</span>
                          <span className="stat-value active">Actif</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="analytics-iframe-container" ref={analyticsContainerRef}>
                <div className="iframe-header">
                  <h3>Rapport Google Analytics</h3>
                  <p className="iframe-description">
                    Pour une vue complète des données, ouvrez Google Analytics dans un nouvel onglet
                  </p>
                </div>
                <div className="analytics-iframe-wrapper">
                  <iframe
                    src={`https://analytics.google.com/analytics/web/#/p${GA_MEASUREMENT_ID.replace('G-', '')}/reports/intelligenthome`}
                    title="Google Analytics"
                    className="analytics-iframe"
                    frameBorder="0"
                    sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                  />
                </div>
                <p className="analytics-note">
                  <strong>Note:</strong> Google Analytics peut bloquer l'affichage dans un iframe pour des raisons de sécurité. 
                  Si le rapport ne s'affiche pas, utilisez les boutons ci-dessus pour ouvrir Google Analytics dans un nouvel onglet.
                </p>
              </div>
            </div>
          </div>

          <div className="analytics-stats-grid">
            <div className="analytics-stat-card">
              <h3>Événements trackés</h3>
              <p className="stat-description">
                Le site track automatiquement les événements suivants :
              </p>
              <ul className="events-list">
                <li>Vues de pages</li>
                <li>Clics sur le menu</li>
                <li>Réservations</li>
                <li>Clics sur les directions</li>
                <li>Appels téléphoniques</li>
                <li>Interactions avec les avis</li>
              </ul>
            </div>

            <div className="analytics-stat-card">
              <h3>Configuration</h3>
              <div className="config-info">
                <div className="config-row">
                  <span className="config-label">Measurement ID:</span>
                  <span className="config-value">{GA_MEASUREMENT_ID}</span>
                </div>
                <div className="config-row">
                  <span className="config-label">Project ID:</span>
                  <span className="config-value">{GA_PROPERTY_ID}</span>
                </div>
                <div className="config-row">
                  <span className="config-label">Type:</span>
                  <span className="config-value">Google Analytics 4 (GA4)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;

